
    import __i18nConfig from '@next-translate-root/i18n.js'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import { releaseFeatures } from "@/config/releaseFeatures";
import LegacyMembershipsPage from "@/modules/legacyMemberships/MembershipsPage";
import React, { useEffect, useRef, useState } from "react";
import useTranslation from "next-translate/useTranslation";
import { useMembership, setMembership } from "../../redux/membership";
import { BASIC_TIER, REGULAR_TIER, UNLIMITED_TIER, FREE_TIER, errorCodes, } from "@chesshotel/constants";
import { Button } from "../../components/ui/button";
import styles from "./Memberships.module.css";
import { FaAward, FaCheck } from "react-icons/fa";
import { SiBitcoin } from "react-icons/si";
import { gaEvent } from "../../lib/ga";
import Head from "next/head";
import Router, { useRouter } from "next/router";
import { setLoadingBar } from "../../redux/siteData";
import { api } from "../../services/api";
import Script from "next/script";
import { useUser, userStore } from "../../redux/user";
import { useMetrica } from "@/lib/next-yandex-metrica";
import RegisterPopup from "@/modules/register/RegisterPopup";
import Popup from "@/components/ui/popup";
import RegisterError from "@/components/common/register-error";
import { validateEmail } from "@/util/validate";
import { checkoutPaywallSource } from "@/services/eventTracker";
import { getTierBenefits } from "@/modules/membership/tierBenefits";
import { redirectToStripeCheckout } from "@/services/stripeCheckout";
const host = process.env.NEXT_PUBLIC_HOST;
const ANNUAL_DISCOUNT_PERCENT = 20;
type BillingPeriod = "monthly" | "yearly";
type DisplayedPricing = {
    perMonth: string;
    yearlyPerMonth: string;
    yearlyTotal: string;
};
type SubscriptionStatus = {
    status?: string;
    cancelAtPeriodEnd?: boolean;
    currentPeriodEnd?: string | null;
};
type CurrencyCode = "USD" | "SEK" | "EUR" | "BRL" | "RUB";
const CURRENCY_BY_LOCALE: Record<string, CurrencyCode> = {
    en: "USD",
    "en-GB": "USD",
    sv: "SEK",
    pt: "BRL",
    ru: "RUB",
};
const YEARLY_TOTALS_BY_TIER: Record<number, Record<CurrencyCode, number>> = {
    [BASIC_TIER]: {
        USD: 28.99,
        EUR: 28.99,
        SEK: 279.0,
        BRL: 94.9,
        RUB: 1890.0,
    },
    [REGULAR_TIER]: {
        USD: 38.99,
        EUR: 38.99,
        SEK: 372.0,
        BRL: 129.9,
        RUB: 2490.0,
    },
    [UNLIMITED_TIER]: {
        USD: 76.99,
        EUR: 76.99,
        SEK: 744.0,
        BRL: 190.9,
        RUB: 4790.0,
    },
};
function getCurrencyByLocale(locale?: string): CurrencyCode {
    if (!locale)
        return "EUR";
    return CURRENCY_BY_LOCALE[locale] || "EUR";
}
function getConfiguredYearlyTotal(tier: number, currency: CurrencyCode): number | null {
    const amount = YEARLY_TOTALS_BY_TIER[tier]?.[currency];
    return typeof amount === "number" ? amount : null;
}
function parsePriceAmount(price: string): number | null {
    const normalized = price.replace(/[^0-9,.-]/g, "");
    if (!normalized)
        return null;
    const hasComma = normalized.includes(",");
    const hasDot = normalized.includes(".");
    const numericCandidate = hasComma && !hasDot
        ? normalized.replace(",", ".")
        : normalized.replace(/,/g, "");
    const amount = Number(numericCandidate);
    if (!Number.isFinite(amount)) {
        return null;
    }
    return amount;
}
function formatCurrencyAmount(amount: number, locale: string, currency: string): string {
    return new Intl.NumberFormat(locale || "en", {
        style: "currency",
        currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    }).format(Math.round(amount * 100) / 100);
}
function hasFractionalPart(amount: number): boolean {
    return Math.abs(amount - Math.trunc(amount)) > 0.000001;
}
function getYearlyDisplayTotal(monthlyPrice: number): number {
    const discountedYearlyTotal = monthlyPrice * ((100 - ANNUAL_DISCOUNT_PERCENT) / 100) * 12;
    // Keep whole-number currencies clean, but use .99 pricing when monthly has cents.
    if (!hasFractionalPart(monthlyPrice)) {
        return Math.round(discountedYearlyTotal);
    }
    const roundedUpWhole = Math.ceil(discountedYearlyTotal);
    return Math.round((roundedUpWhole - 0.01) * 100) / 100;
}
function getDisplayedPricing(tier: number, monthlyPrice: string, locale: string): DisplayedPricing {
    const parsedMonthlyPrice = parsePriceAmount(monthlyPrice);
    const currency = getCurrencyByLocale(locale);
    if (parsedMonthlyPrice == null) {
        return {
            perMonth: monthlyPrice,
            yearlyPerMonth: monthlyPrice,
            yearlyTotal: monthlyPrice,
        };
    }
    const configuredYearlyTotal = getConfiguredYearlyTotal(tier, currency);
    const yearlyTotalPrice = configuredYearlyTotal ?? getYearlyDisplayTotal(parsedMonthlyPrice);
    return {
        perMonth: formatCurrencyAmount(parsedMonthlyPrice, locale, currency),
        yearlyPerMonth: formatCurrencyAmount(yearlyTotalPrice / 12, locale, currency),
        yearlyTotal: formatCurrencyAmount(yearlyTotalPrice, locale, currency),
    };
}
export const STRIPE_PUBLISHABLE_KEY = host === "www.chesshotel.com"
    ? "pk_live_qeEylFbhs0L00dSjfkv38sbQ"
    : "pk_test_NHIqO56sCnPR51BfuTa1dtm6";
const Check = () => (<div className={styles.check}>
    <div className={`flex justify-center items-center w-5 h-5 rounded-full ${styles.checkCircle}`}>
      <div className={`flex items-center justify-center w-2.5 h-2.5 ${styles.checkIcon}`}>
        <FaCheck />
      </div>
    </div>
  </div>);
export function selectPlan(tier, billingPeriod: BillingPeriod = "monthly", onEmailRequired?: () => void, onCheckoutError?: () => void) {
    setLoadingBar(true);
    const locale = Router.locale;
    return api
        .post("/stripe/session", { tier, locale, billingPeriod, paywallSource: checkoutPaywallSource(userStore.getState().id) })
        .then((result) => {
        setLoadingBar(false);
        redirectToStripeCheckout(result.data.url);
        // gaEvent("Memberships", "Memberships", tier);
    })
        .catch((error) => {
        setLoadingBar(false);
        if (error?.response?.data?.code === "TRIAL_EMAIL_REQUIRED" &&
            onEmailRequired) {
            onEmailRequired();
            return;
        }
        const message = error?.response?.data?.error;
        if (message) {
            alert(message);
        }
        else {
            onCheckoutError?.();
        }
    });
}
export function changePlan(tier, billingPeriod: BillingPeriod = "monthly") {
    setLoadingBar(true);
    const locale = Router.locale;
    api
        .post("/stripe/change", { tier, locale, billingPeriod })
        .then((response) => {
        setLoadingBar(false);
        const { membership } = response.data;
        setMembership(membership);
        Router.push("/memberships");
    })
        .catch((e) => {
        setLoadingBar(false);
    });
}
enum PaymentMethod {
    ALL = "all",
    CRYPTO = "crypto",
    STRIPE = "stripe"
}
const CRYPTO_CHECKOUT_ENABLED = process.env.NEXT_PUBLIC_ENABLE_CRYPTO_CHECKOUT === "true";
function getPaymentMethod(geo: string): PaymentMethod {
    if (!CRYPTO_CHECKOUT_ENABLED) {
        return PaymentMethod.STRIPE;
    }
    if (geo === "RU") {
        return PaymentMethod.CRYPTO;
    }
    else if (geo === "UA" || geo === "BY" || geo === "KZ") {
        return PaymentMethod.ALL;
    }
    return PaymentMethod.STRIPE;
}
export function selectPlanNowPayments(tier, billingPeriod: BillingPeriod = "monthly") {
    setLoadingBar(true);
    const locale = Router.locale;
    api
        .post("/now-payments/invoice", { tier, locale, billingPeriod })
        .then((result) => {
        setLoadingBar(false);
        const { invoiceUrl } = result.data;
        if (invoiceUrl) {
            window.location.href = invoiceUrl;
        }
    })
        .catch(() => {
        setLoadingBar(false);
    });
}
export function selectPlanCryptomus(tier, billingPeriod: BillingPeriod = "monthly") {
    setLoadingBar(true);
    const locale = Router.locale;
    api
        .post("/cryptomus/invoice", { tier, locale, billingPeriod })
        .then((result) => {
        setLoadingBar(false);
        const { invoiceUrl } = result.data;
        if (invoiceUrl) {
            window.location.href = invoiceUrl;
        }
    })
        .catch(() => {
        setLoadingBar(false);
    });
}
function Memberships() {
    const { t } = useTranslation("common");
    const { t: authT } = useTranslation("auth");
    const router = useRouter();
    const { tier, products, subscriptionStatus, subscription, trialEligible, } = useMembership() as {
        tier: number;
        products: Record<number, Record<string, string>>;
        subscriptionStatus?: SubscriptionStatus | null;
        subscription?: string | null;
        trialEligible?: boolean | null;
    };
    const { memberLoggedIn, geo } = useUser();
    const paymentMethod = getPaymentMethod(geo);
    const { reachGoal } = useMetrica();
    const [showRegisterPopup, setShowRegisterPopup] = useState(false);
    // Annual offers are paused until their Stripe prices are configured.
    const billingPeriod: BillingPeriod = "monthly";
    const guestSelectedPlan = useRef<number | null>(null);
    const [emailCheckout, setEmailCheckout] = useState<{
        tier: number;
        billingPeriod: BillingPeriod;
    } | null>(null);
    const [checkoutEmail, setCheckoutEmail] = useState("");
    const [checkoutEmailError, setCheckoutEmailError] = useState<string | null>(null);
    const [savingCheckoutEmail, setSavingCheckoutEmail] = useState(false);
    let basicPrice, regularPrice, unlimitedPrice;
    let buttonText;
    const locale = router.locale || "en";
    const perMonthSuffix = t("/month");
    const mostPopularLabel = t("Most popular");
    useEffect(() => {
        if (!memberLoggedIn) {
            return;
        }
        api
            .get("/membership")
            .then((response) => {
            const latestMembership = response?.data?.membership;
            if (latestMembership) {
                setMembership(latestMembership);
            }
        })
            .catch(() => {
            // Keep current page usable even if refresh fails.
        });
    }, [memberLoggedIn]);
    function formatMembershipDate(value: string | null | undefined): string {
        if (!value) {
            return "";
        }
        const date = new Date(value);
        if (isNaN(date.getTime())) {
            return "";
        }
        return new Intl.DateTimeFormat(locale, {
            year: "numeric",
            month: "long",
            day: "numeric",
        }).format(date);
    }
    const cancellationEndDate = formatMembershipDate(subscriptionStatus?.currentPeriodEnd);
    const cancellationScheduled = Boolean(memberLoggedIn &&
        tier !== FREE_TIER &&
        subscriptionStatus?.cancelAtPeriodEnd);
    basicPrice = products[BASIC_TIER][locale] || "$2.99";
    regularPrice = products[REGULAR_TIER][locale] || "$3.99";
    unlimitedPrice = products[UNLIMITED_TIER][locale] || "$7.99";
    const basicPricing = getDisplayedPricing(BASIC_TIER, basicPrice, locale);
    const regularPricing = getDisplayedPricing(REGULAR_TIER, regularPrice, locale);
    const unlimitedPricing = getDisplayedPricing(UNLIMITED_TIER, unlimitedPrice, locale);
    const basicBenefits = getTierBenefits(t, BASIC_TIER);
    const regularBenefits = getTierBenefits(t, REGULAR_TIER);
    const unlimitedBenefits = getTierBenefits(t, UNLIMITED_TIER);
    const canStartFreeTrial = !memberLoggedIn || (trialEligible !== false && !subscription);
    const startCheckout = (plan: number, period: BillingPeriod) => selectPlan(plan, period, () => {
        setCheckoutEmailError(null);
        setEmailCheckout({ tier: plan, billingPeriod: period });
    }, () => alert(authT("Something went wrong")));
    const saveCheckoutEmail = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!emailCheckout || savingCheckoutEmail)
            return;
        const selectedCheckout = emailCheckout;
        setSavingCheckoutEmail(true);
        setCheckoutEmailError(null);
        try {
            const email = checkoutEmail.trim();
            const validationError = await validateEmail(email);
            if (validationError) {
                setCheckoutEmailError(validationError);
                return;
            }
            await api.post("/users/email", { email });
            setEmailCheckout(null);
            await startCheckout(selectedCheckout.tier, selectedCheckout.billingPeriod);
        }
        catch (error) {
            setCheckoutEmailError(error?.response?.data === "ERROR_EMAIL_NOT_AVAILABLE"
                ? errorCodes.ERROR_EMAIL_NOT_AVAILABLE
                : errorCodes.ERROR_GENERAL);
        }
        finally {
            setSavingCheckoutEmail(false);
        }
    };
    buttonText =
        tier === FREE_TIER
            ? canStartFreeTrial
                ? t("Start free trial")
                : t("Get premium")
            : authT("Change membership");
    const handlePlanClick = (plan) => {
        if (!memberLoggedIn) {
            guestSelectedPlan.current = plan;
            setShowRegisterPopup(true);
            return;
        }
        gaEvent({
            category: "Memberships",
            action: "ClickOnPlan",
            label: "plan: " + plan + ", period: " + billingPeriod,
        });
        reachGoal("membership_step_memberships_page_click");
        if (tier === FREE_TIER) {
            startCheckout(plan, billingPeriod);
        }
        else {
            changePlan(plan, billingPeriod);
        }
    };
    const handleCryptoPlanClick = (plan) => {
        if (!memberLoggedIn) {
            router.push("/signIn");
            return;
        }
        gaEvent({
            category: "Memberships",
            action: "ClickOnCryptoPlan",
            label: "plan: " + plan + ", period: " + billingPeriod,
        });
        reachGoal("membership_crypto_click");
        selectPlanCryptomus(plan, billingPeriod);
    };
    function getButtons(plan: number) {
        const membershipButtonVariant = plan === UNLIMITED_TIER ? "default" : "secondary";
        const isCurrentPlan = memberLoggedIn && tier === plan;
        if (isCurrentPlan) {
            return (<div className={styles.currentPlanIndicator}>{t("Current plan")}</div>);
        }
        if (paymentMethod === PaymentMethod.STRIPE) {
            return (<Button size="lg" variant={membershipButtonVariant} className="font-bold" onClick={() => handlePlanClick(plan)}>
          {buttonText}
        </Button>);
        }
        else if (paymentMethod === PaymentMethod.CRYPTO) {
            return (<Button size="lg" className="space-x-2" onClick={() => handleCryptoPlanClick(plan)}>
          <SiBitcoin className={styles.cryptoIcon}/>
          <span>{t("Crypto / Local")}</span>
        </Button>);
        }
        else
            return (<>
          <Button size="lg" variant={membershipButtonVariant} onClick={() => handlePlanClick(plan)}>
            {buttonText}
          </Button>
          <Button size="lg" variant="secondary" className="space-x-2" onClick={() => handleCryptoPlanClick(plan)}>
            <SiBitcoin className={styles.cryptoIcon}/>
            <span>{t("Crypto / Local")}</span>
          </Button>
        </>);
    }
    return (<div className={styles.bg}>
      <Script strategy="lazyOnload" src="https://js.stripe.com/v3/"/>
      <Head>
        <title>{"Chess Hotel | " + t("Premium memberships")}</title>
        <meta name="description" content={t("description")}/>
        <meta name="keywords" content={t("keywords")}/>
      </Head>
      <div className={styles.container}>
        <div className={styles.header}>
          <div className="flex items-center gap-2 mb-2 text-3xl font-semibold">
            <div className="text-[0.85em] text-orange-400">
              <FaAward />
            </div>
            <span>{t("Premium memberships")}</span>
          </div>
        </div>
        {cancellationScheduled && (<div className={styles.subscriptionNotice}>
            <div className={styles.subscriptionNoticeTitle}>
              {authT("Subscription cancellation scheduled")}
            </div>
            <div className={styles.subscriptionNoticeText}>
              {cancellationEndDate
                ? `${authT("Membership ends on")} ${cancellationEndDate}`
                : t("Your premium access stays active until the end of the current billing period.")}
            </div>
          </div>)}
        <div className={styles.tiersCont}>
          <div className={styles.tier}>
            <div className={styles.basicHeader}>
              <span className={styles.planName}>BASIC</span>
            </div>
            <div className={styles.tierBody}>
              <div className={styles.tierTitle}>
                <span className={styles.priceAmount}>
                  {basicPricing.perMonth}
                </span>
                <span className={styles.priceSuffix}>{perMonthSuffix}</span>
              </div>
              {basicBenefits.map((benefit) => (<div key={benefit} className={styles.basicItem}>
                  <Check />
                  {benefit}
                </div>))}
              <div className={styles.buttonsCont}>{getButtons(BASIC_TIER)}</div>
            </div>
          </div>
          <div className={styles.tier}>
            <div className={styles.regularHeader}>
              <span className={styles.planName}>REGULAR</span>
            </div>
            <div className={styles.tierBody}>
              <div className={styles.tierTitle}>
                <span className={styles.priceAmount}>
                  {regularPricing.perMonth}
                </span>
                <span className={styles.priceSuffix}>{perMonthSuffix}</span>
              </div>
              {regularBenefits.map((benefit) => (<div key={benefit} className={styles.basicItem}>
                  <Check />
                  {benefit}
                </div>))}
              <div className={styles.buttonsCont}>
                {getButtons(REGULAR_TIER)}
              </div>
            </div>
          </div>
          <div className={`${styles.tier} `}>
            <div className={styles.unlimitedHeader}>
              <span className={styles.planName}>UNLIMITED</span>
              <span className={styles.mostPopularBadge}>
                {mostPopularLabel}
              </span>
            </div>
            <div className={styles.tierBody}>
              <div className={styles.tierTitle}>
                <span className={styles.priceAmount}>
                  {unlimitedPricing.perMonth}
                </span>
                <span className={styles.priceSuffix}>{perMonthSuffix}</span>
              </div>
              {unlimitedBenefits.map((benefit) => (<div key={benefit} className={styles.basicItem}>
                  <Check />
                  {benefit}
                </div>))}
              <div className={styles.buttonsCont}>
                {getButtons(UNLIMITED_TIER)}
              </div>
            </div>
          </div>
        </div>
        <div className={styles.header2}>
          {t("Get a premium membership and improve your game!")}
        </div>
        {canStartFreeTrial && (<div className={styles.freeTrial}>
            {t("All memberships starts with a 7 day free trial")}
          </div>)}
      </div>
      {showRegisterPopup && (<RegisterPopup onClose={() => {
                setShowRegisterPopup(false);
            }} redirectAfterDone={() => {
                if (guestSelectedPlan.current != null) {
                    startCheckout(guestSelectedPlan.current, billingPeriod);
                }
                setShowRegisterPopup(false);
            }}/>)}
      {emailCheckout && (<Popup title={t("Email")} subtitle={t("Add an email address to check your free-trial eligibility.")} onClose={savingCheckoutEmail ? null : () => setEmailCheckout(null)} maxWidth="464px">
          <form onSubmit={saveCheckoutEmail} className="flex flex-col gap-4">
            <label htmlFor="checkout-email">{t("Email")}</label>
            <input id="checkout-email" type="email" autoComplete="email" autoFocus required maxLength={50} value={checkoutEmail} disabled={savingCheckoutEmail} onChange={(event) => setCheckoutEmail(event.target.value)} aria-invalid={Boolean(checkoutEmailError)} aria-describedby={checkoutEmailError ? "checkout-email-error" : undefined} className="rounded-md border border-altgray-200 p-2.5"/>
            <RegisterError id="checkout-email-error" errorCode={checkoutEmailError}/>
            <Button type="submit" disabled={savingCheckoutEmail}>
              {t("Continue")}
            </Button>
          </form>
        </Popup>)}
    </div>);
}
export default releaseFeatures.paywalls ? Memberships : LegacyMembershipsPage;

    async function __Next_Translate__getStaticProps__1a1751eb798__(ctx) {
      if (typeof global !== 'undefined') {
        global.i18nConfig = __i18nConfig
      }
      
      return {
        
        
        props: {
          
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/memberships/index',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || (() => Promise.resolve({})),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__1a1751eb798__ as getStaticProps }
  